/**
 * Warehouse API Functions
 *
 * API functions for warehouse CRUD operations and KPIs
 */

import type {
  Warehouse,
  CreateWarehouseDto,
  UpdateWarehouseDto,
  WarehouseKpis,
} from '@lognai/plugin-warehouse';
import type { PaginationParams, PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildPaginationQuery } from './client';

const BASE_URL = '/api/warehouse';

/**
 * Paginated list response
 */
export interface PaginatedWarehouseList {
  items: Warehouse[];
  pagination: PaginationMetadata | null;
}

/**
 * Get list of all warehouses with pagination
 */
export const listWarehouses = async (
  params?: PaginationParams
): Promise<PaginatedWarehouseList> => {
  const query = buildPaginationQuery(params);
  return apiFetch<PaginatedWarehouseList>(`${BASE_URL}${query}`);
};

/**
 * Get a single warehouse by ID
 */
export const getWarehouse = async (id: string): Promise<Warehouse> => {
  return apiFetch<Warehouse>(`${BASE_URL}/${id}`);
};

/**
 * Create a new warehouse
 */
export const createWarehouse = async (dto: CreateWarehouseDto): Promise<Warehouse> => {
  return apiFetch<Warehouse>(BASE_URL, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
};

/**
 * Update an existing warehouse
 */
export const updateWarehouse = async (
  id: string,
  dto: UpdateWarehouseDto
): Promise<Warehouse> => {
  return apiFetch<Warehouse>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
};

/**
 * Delete a warehouse
 */
export const deleteWarehouse = async (id: string): Promise<void> => {
  return apiFetch<void>(`${BASE_URL}/${id}`, {
    method: 'DELETE',
  });
};

/**
 * Get warehouse KPIs (total count, active count, total capacity)
 */
export const getWarehouseKpis = async (): Promise<WarehouseKpis> => {
  return apiFetch<WarehouseKpis>(`${BASE_URL}/kpis`);
};
